import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import {
  FiArrowRight,
  FiAward,
  FiBarChart,
  FiCheckCircle,
  FiEye,
  FiFileText,
  FiFolder,
  FiHome,
  FiShield,
  FiStar,
  FiSun,
  FiTrendingUp,
  FiUser,
  FiZap,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { getRandomQuote } from "../../data/inspirationalQuotes";
import { useProfile } from "../../hooks/useProfile";
import ProfileCompletePrompt from "./ProfileCompletePrompt";

export default function ActiveUserDashboard({
  applications = [],
  userStats = {},
  profileCompletionPercentage = 85, // This would come from your backend
  showProfileCompletePrompt = false, // This would be determined by your logic
}) {
  // Get profile data for user name
  const { data: profileData, isLoading: profileLoading } = useProfile();

  const navigate = useNavigate();
  const [showPrompt, setShowPrompt] = useState(showProfileCompletePrompt);
  const [currentQuote, setCurrentQuote] = useState(getRandomQuote());

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const subtextColor = useColorModeValue("gray.600", "gray.300");

  // Use calculated stats from parent component
  const stats = {
    jdsAnalyzed: userStats.jdsAnalyzed || 0,
    resumesDownloaded: userStats.resumesDownloaded || 0,
    avgMatchScore: userStats.avgMatchScore || 0,
    totalApplications: userStats.totalApplications || applications.length || 0,
  };

  // Get profile name with fallback - handle both object and array formats
  const getProfileName = () => {
    if (profileLoading) return "User";

    // Handle array format (profileData is Array(1))
    if (Array.isArray(profileData) && profileData.length > 0) {
      const profile = profileData[0];
      return (
        profile?.first_name || profile?.fullName || profile?.name || "User"
      );
    }

    // Handle object format
    return (
      profileData?.first_name ||
      profileData?.fullName ||
      profileData?.name ||
      "User"
    );
  };

  const profileName = getProfileName();

  // Check for profile completion on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const justCompleted = urlParams.get("profile_completed") === "true";
    const hasSeenPrompt = localStorage.getItem("profile_complete_prompt_seen");

    if (
      (justCompleted || profileCompletionPercentage >= 100) &&
      !hasSeenPrompt
    ) {
      setShowPrompt(true);
      // Clean up URL if it has the parameter
      if (justCompleted) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    }
  }, [profileCompletionPercentage]);

  // Get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning!";
    if (hour < 17) return "Good afternoon!";
    return "Good evening!";
  };

  const handleDismissPrompt = () => {
    setShowPrompt(false);
    localStorage.setItem("profile_complete_prompt_seen", "true");
  };

  const handleViewMasterResume = () => {
    localStorage.setItem("profile_complete_prompt_seen", "true");
    navigate("/me/resume");
  };

  const quickActions = [
    {
      icon: FiStar,
      title: "Analyze Applications",
      description: "Get algorithmic match scores for job descriptions",
      action: () => navigate("/me/applications"),
    },
    {
      icon: FiUser,
      title: "Update Work History",
      description: "Add new experiences to your profile",
      action: () => navigate("/me/work-experience"),
    },
    {
      icon: FiEye,
      title: "Preview Master Resume",
      description: "View your complete profile",
      action: () => navigate("/me/profile/resume-preview"),
      isHighlighted: profileCompletionPercentage >= 100, // Highlight when complete
    },
  ];

  return (
    <Box px={8} py={3} bg="gray.50">
      <VStack spacing={3} align="stretch">
        {/* Page Title with Accent Bar */}
        <Flex align="center" mb={2}>
          <Box w="4px" h="28px" bg="gray.900" borderRadius="full" mr={4} />
          <HStack spacing={3}>
            <Icon as={FiHome} color="gray.900" boxSize={5} />
            <Text
              fontSize="xl"
              fontWeight="600"
              color="gray.900"
              letterSpacing="-0.025em"
            >
              Hello, {profileName}
            </Text>
          </HStack>
        </Flex>

        {/* 0. Profile Complete Prompt */}
        {showPrompt && (
          <ProfileCompletePrompt
            onViewMasterResume={handleViewMasterResume}
            onDismiss={handleDismissPrompt}
            basicInfo={{}} // You would pass actual user data here
          />
        )}

        {/* 1. Clean Greeting Card */}
        <Flex justify="space-between" align="center" gap={4}>
          <Box
            bg="white"
            p={6}
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.100"
            _hover={{
              borderColor: "orange.200",
              shadow: "lg",
              transform: "translateY(-2px)",
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            flex="1"
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.06)"
          >
            <HStack spacing={4}>
              <Box
                p={3}
                bg="orange.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="orange.100"
              >
                <Icon as={FiSun} color="orange.500" boxSize={5} />
              </Box>
              <VStack align="start" spacing={1}>
                <Text
                  fontSize="xl"
                  fontWeight="600"
                  color={textColor}
                  letterSpacing="-0.025em"
                >
                  {getTimeBasedGreeting()}
                </Text>
                <Text fontSize="sm" color={subtextColor} fontWeight="500">
                  Your professional profile is performing excellently.
                </Text>
              </VStack>
            </HStack>
          </Box>

          {/* Clean Professional Badge */}
          <Box
            bg="white"
            p={5}
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.100"
            _hover={{
              borderColor: "orange.200",
              shadow: "lg",
              transform: "translateY(-2px)",
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            minW="200px"
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.06)"
            position="relative"
            overflow="hidden"
          >
            {/* Simple top accent */}
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              h="3px"
              bg="orange.500"
              borderRadius="xl xl 0 0"
            />

            <VStack spacing={3} pt={2}>
              <HStack spacing={2}>
                <Icon as={FiAward} color="orange.500" boxSize={4} />
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="600"
                  letterSpacing="0.05em"
                  textTransform="uppercase"
                >
                  Professional
                </Text>
              </HStack>

              <VStack spacing={1}>
                <Text
                  fontSize="3xl"
                  fontWeight="700"
                  color="orange.500"
                  letterSpacing="-0.02em"
                  lineHeight="1"
                >
                  {stats.avgMatchScore}%
                </Text>
                <Text
                  fontSize="xs"
                  color="gray.500"
                  textAlign="center"
                  fontWeight="500"
                >
                  Average Match Score
                </Text>
              </VStack>
            </VStack>
          </Box>
        </Flex>

        {/* 2. Stats Cards */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <StatCard
            title="JDs Analyzed"
            value={stats.jdsAnalyzed}
            icon={FiFileText}
            iconColor="blue.500"
          />
          <StatCard
            title="Resumes Downloaded"
            value={stats.resumesDownloaded}
            icon={FiCheckCircle}
            iconColor="green.500"
          />
          <StatCard
            title="Avg Match Score"
            value={`${stats.avgMatchScore}%`}
            icon={FiBarChart}
            iconColor="purple.500"
          />
          <StatCard
            title="Total Applications"
            value={stats.totalApplications}
            icon={FiFolder}
            iconColor="orange.500"
          />
        </SimpleGrid>

        {/* 3. Quick Actions Card */}
        <Box
          bg={bgColor}
          p={6}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          _hover={{
            borderColor: "gray.300",
          }}
          transition="all 0.2s ease"
        >
          <VStack align="start" spacing={5}>
            <Box>
              <HStack spacing={2} mb={2}>
                <Icon as={FiZap} color="orange.500" boxSize={5} />
                <Text fontSize="lg" fontWeight="medium" color={textColor}>
                  Quick Actions
                </Text>
              </HStack>
              <Text fontSize="sm" color={subtextColor}>
                Professional optimization and analysis tools
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} w="full">
              {quickActions.map((action, index) => (
                <QuickActionCard
                  key={index}
                  action={action}
                  onClick={action.action}
                />
              ))}
            </SimpleGrid>
          </VStack>
        </Box>

        {/* 4. Eye-catching Quote Card */}
        <Box
          bg="linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)"
          p={4}
          borderRadius="xl"
          border="2px solid"
          borderColor="orange.200"
          borderLeft="4px solid"
          borderLeftColor="orange.500"
          _hover={{
            borderColor: "orange.300",
            shadow: "lg",
            transform: "translateY(-1px)",
          }}
          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          boxShadow="0 4px 20px rgba(251, 146, 60, 0.1)"
          position="relative"
          overflow="hidden"
        >
          {/* Subtle background pattern */}
          <Box
            position="absolute"
            top="0"
            right="0"
            w="80px"
            h="80px"
            bg="radial-gradient(circle, rgba(251, 146, 60, 0.05) 0%, transparent 70%)"
            borderRadius="full"
            transform="translate(20px, -20px)"
          />

          <HStack spacing={3} align="start" position="relative">
            <Box
              p={2}
              bg="orange.100"
              borderRadius="lg"
              border="1px solid"
              borderColor="orange.200"
            >
              <Icon as={FiStar} color="orange.600" boxSize={4} />
            </Box>
            <Box flex={1}>
              <Text
                fontSize="sm"
                color="gray.700"
                fontStyle="italic"
                mb={2}
                lineHeight="1.5"
                fontWeight="500"
              >
                "{currentQuote.quote}"
              </Text>
              <Text fontSize="xs" color="orange.600" fontWeight="600">
                — {currentQuote.author}
              </Text>
            </Box>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
}

function StatCard({ title, value, icon, iconColor }) {
  const textColor = useColorModeValue("gray.800", "white");
  const subtextColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box
      bg="white"
      p={4}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.100"
      _hover={{
        borderColor: "gray.200",
        shadow: "md",
        transform: "translateY(-1px)",
      }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      boxShadow="0 2px 12px rgba(0, 0, 0, 0.04)"
      position="relative"
      overflow="hidden"
    >
      {/* Subtle background pattern */}
      <Box
        position="absolute"
        top="0"
        right="0"
        w="60px"
        h="60px"
        bg={`radial-gradient(circle, ${iconColor}15 0%, transparent 70%)`}
        borderRadius="full"
        transform="translate(20px, -20px)"
      />

      <VStack spacing={2} align="start" position="relative">
        <HStack spacing={2} w="full">
          <Icon as={icon} color={iconColor} boxSize={4} />
          <Text
            fontSize="xs"
            fontWeight="600"
            color={subtextColor}
            flex={1}
            letterSpacing="0.025em"
          >
            {title}
          </Text>
        </HStack>
        <Text
          fontSize="xl"
          fontWeight="700"
          color={textColor}
          letterSpacing="-0.025em"
        >
          {value}
        </Text>
      </VStack>
    </Box>
  );
}

function QuickActionCard({ action, onClick }) {
  const textColor = useColorModeValue("gray.800", "white");
  const subtextColor = useColorModeValue("gray.600", "gray.300");

  return (
    <Box
      p={3}
      bg="white"
      border="1px solid"
      borderColor="gray.100"
      borderRadius="lg"
      cursor="pointer"
      onClick={onClick}
      _hover={{
        shadow: "md",
        transform: "translateY(-2px)",
        borderColor: action.isHighlighted ? "orange.200" : "gray.200",
        bg: action.isHighlighted ? "orange.25" : "gray.25",
      }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      boxShadow="0 2px 8px rgba(0, 0, 0, 0.04)"
      textAlign="center"
      position="relative"
      overflow="hidden"
    >
      {/* Premium gradient overlay for highlighted actions */}
      {action.isHighlighted && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          h="2px"
          bg="linear-gradient(90deg, #FF6B35 0%, #F7931E 100%)"
        />
      )}

      <VStack spacing={2}>
        <Box
          w={8}
          h={8}
          bg={action.isHighlighted ? "orange.50" : "gray.50"}
          borderRadius="lg"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor={action.isHighlighted ? "orange.100" : "gray.100"}
          position="relative"
          transition="all 0.2s ease"
        >
          <Icon
            as={action.icon}
            color={action.isHighlighted ? "orange.500" : "gray.500"}
            boxSize={4}
          />
          {action.isHighlighted && (
            <Box
              position="absolute"
              top="-1px"
              right="-1px"
              w={2}
              h={2}
              bg="green.400"
              borderRadius="full"
              border="1px solid white"
            />
          )}
        </Box>

        <VStack spacing={1}>
          <Text
            fontSize="xs"
            fontWeight="600"
            color={textColor}
            letterSpacing="-0.025em"
          >
            {action.title}
          </Text>
          <Text
            fontSize="xs"
            color={subtextColor}
            lineHeight="1.3"
            maxW="120px"
          >
            {action.description}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
}
